import { useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import {
  addCollection,
  updateCollectionDetails,
  setCollectionImageUploaded,
  generateId,
  useT,
  useAppDispatch,
  type Collection,
} from "@reciperoll/core";
import { useAuth } from "../lib/auth";
import { useToast } from "./Toast";
import { StorageImage } from "./StorageImage";
import { uploadCollectionImage } from "../utils/uploadCollectionImage";

const inputClass =
  "w-full rounded-lg border border-line px-3 py-2 text-ink outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta";
const labelClass = "block text-sm font-semibold text-ink mb-1";

// Create/edit a collection (name, description, cover image). Modal styled like the
// other web dialogs.
export function CollectionForm({
  collection,
  previewImage,
  onClose,
  onCreated,
}: {
  collection?: Collection;
  // The collection's resolved cover shown on its card (own cover, else a recipe
  // photo). Used only for the preview when there's no uploaded cover of its own.
  previewImage?: string;
  onClose: () => void;
  // Called with the new collection's id after a successful create (not on edit) —
  // lets the caller navigate into the freshly created, empty collection.
  onCreated?: (collectionId: string) => void;
}) {
  const t = useT();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { session } = useAuth();
  const userId = session?.user.id;
  const isEdit = !!collection;
  // Pre-assigned id (so an uploaded image under it matches on save) for create.
  const collectionId = useRef(collection?.id ?? generateId()).current;

  const [name, setName] = useState(collection?.name ?? "");
  const [description, setDescription] = useState(collection?.description ?? "");
  const [image, setImage] = useState(collection?.image ?? "");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !userId) return;
    setUploading(true);
    setError(null);
    try {
      setImage(await uploadCollectionImage(userId, collectionId, file));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("form.imageUploadFailed"));
      Sentry.captureException(err, {
        tags: { area: "storage", op: "image-upload", bucket: "collection-images" },
      });
    } finally {
      setUploading(false);
    }
  }

  function save() {
    if (!name.trim()) {
      setError(t("collections.giveName"));
      return;
    }
    const trimmedName = name.trim();
    const trimmedDesc = description.trim();
    if (isEdit) {
      dispatch(updateCollectionDetails({ id: collectionId, name: trimmedName, description: trimmedDesc }));
    } else {
      dispatch(addCollection({ id: collectionId, name: trimmedName, description: trimmedDesc || null }));
    }
    // Set the cover if it changed (the collection row now exists either way).
    if (image && image !== (collection?.image ?? "")) {
      dispatch(setCollectionImageUploaded({ id: collectionId, url: image }));
    }
    toast.show(isEdit ? t("collections.updated") : t("collections.created"));
    onClose();
    if (!isEdit) onCreated?.(collectionId);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-surface p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold text-ink mb-4">
          {isEdit ? t("collections.editCollection") : t("collections.newCollection")}
        </h2>
        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

        <div className="space-y-4">
          <div>
            <label className={labelClass}>{t("form.name")}</label>
            <input
              className={inputClass}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("collections.namePlaceholder")}
              autoFocus
            />
          </div>
          <div>
            <label className={labelClass}>{t("form.description")}</label>
            <textarea
              className={`${inputClass} text-sm`}
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("collections.optional")}
            />
          </div>
          <div>
            <label className={labelClass}>{t("collections.coverImage")}</label>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-xl bg-surface-2 overflow-hidden flex items-center justify-center shrink-0">
                <StorageImage
                  stored={image}
                  fallback={
                    previewImage ? (
                      // No custom cover — show the recipe-derived fallback, dimmed.
                      <StorageImage
                        stored={previewImage}
                        imgClassName="w-full h-full object-cover opacity-50"
                      />
                    ) : (
                      <span className="text-xs text-ink-faint">{t("collections.none")}</span>
                    )
                  }
                />
              </div>
              <div className="flex-1 min-w-0">
                <label className="inline-block cursor-pointer rounded-lg bg-surface-2 hover:bg-line px-4 py-2 text-sm font-semibold text-ink">
                  {uploading ? t("form.uploading") : image ? t("form.replaceImage") : t("form.uploadImage")}
                  <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} />
                </label>
                {!image && previewImage && (
                  <p className="text-xs text-ink-faint mt-1.5">
                    {t("collections.noCustomCover")}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 rounded-xl border border-line py-2.5 font-semibold text-ink-soft hover:bg-surface-2 cursor-pointer"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={save}
            className="flex-1 rounded-xl bg-terracotta hover:bg-terracotta-dark transition-colors py-2.5 font-semibold text-white cursor-pointer"
          >
            {isEdit ? t("common.save") : t("collections.create")}
          </button>
        </div>
      </div>
    </div>
  );
}
